import { datadogRum } from '@datadog/browser-rum-slim';
import { MONITORING_TAGS } from 'constants/monitoring';
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import { usePersistStorageState } from 'state/persistStorage';
import { Session } from './schema';
import { parseRawSession } from './utils';

/**
 * Only use this hook in the "appLayer/session" module.
 */
export const useSession = () => {
  const [getRawSession, setRawSession] = usePersistStorageState('session');
  const rawSession = getRawSession();

  const session = useMemo(() => {
    try {
      if (!rawSession) {
        return null;
      }

      const parsedSession = parseRawSession(rawSession);

      if (!parsedSession) {
        setRawSession(null);
      }

      return parsedSession;
    } catch (error) {
      const tags = {
        [MONITORING_TAGS.AUTH]: true
      };
      datadogRum.addError(error, { tags });

      setRawSession(null);

      return null;
    }
  }, [rawSession, setRawSession]);

  const setSession = useCallback(
    (newSession: Session | null) => setRawSession(newSession ? JSON.stringify(newSession) : null),
    [setRawSession]
  );

  return [session, setSession] as const;
};

export const useSessionValue = () => {
  const [session] = useSession();

  return session;
};

/**
 * This defers the session value until the next render.
 *
 * Use this function whenever you need to use the session value for UI on
 * non-member pages. Since the server doesn't have access to the session, and
 * therefore can't use it for rendering, you will get hydration mismatches if
 * you use the session value directly.
 */
export const useDeferredSessionValue = () => {
  const [session, setSession] = useState<ReturnType<typeof useSessionValue>>(null);
  const sessionVal = useSessionValue();

  useEffect(() => {
    // Since this code can actually be called during a hydration, we need to
    // batch it up to be performed afterwards. Starting the transition should do
    // this.
    startTransition(() => {
      setSession(sessionVal);
    });
  }, [sessionVal]);

  return session;
};

export const useClearSession = () => {
  const [, setSession] = useSession();

  return useCallback(() => setSession(null), [setSession]);
};
